/**
 * Mémoire de l'aide contextuelle — PAR UTILISATEUR, dans le navigateur.
 *
 * Aucun endpoint : la progression (bulles comprises, visites terminées,
 * premiers pas cochés) vit sous la clé `mada.help.<userId>`. Un autre compte
 * sur le même poste repart de zéro. Stockage indisponible (navigation
 * privée) : l'aide fonctionne, sans mémoire. Jamais de token ici (INV-5).
 */
import { create } from "zustand";

export const HELP_STORAGE_PREFIX = "mada.help.";
export const ANONYMOUS_KEY = "anonymous";

export interface ActiveTour {
  id: string;
  step: number;
}

export interface HelpSnapshot {
  /** Sujets dont l'utilisateur a cliqué « Compris » (balise éteinte). */
  dismissed: Record<string, true>;
  /** Visites terminées : id → date ISO. */
  completedTours: Record<string, string>;
  /** Premiers pas cochés (manuellement ou par visite terminée). */
  checklist: Record<string, true>;
  /** Carte « Premiers pas » masquée. */
  checklistHidden: boolean;
  /** Carte « Premiers pas » demandée explicitement (menu Aide), même sur un
   *  compte déjà rodé où elle ne s'affiche plus d'elle-même. */
  checklistShown: boolean;
  /** Fenêtre de bienvenue déjà vue. */
  welcomeSeen: boolean;
  /** Balises pulsantes visibles (false = seules les icônes « ? » restent). */
  beacons: boolean;
  /** Visite en cours (reprise après rechargement). */
  activeTour: ActiveTour | null;
}

const EMPTY: HelpSnapshot = {
  dismissed: {},
  completedTours: {},
  checklist: {},
  checklistHidden: false,
  checklistShown: false,
  welcomeSeen: false,
  beacons: true,
  activeTour: null,
};

interface HelpState extends HelpSnapshot {
  /** Utilisateur dont la mémoire est chargée (null avant hydratation). */
  userKey: string | null;
  hydrate: (userKey: string) => void;
  dismiss: (topicId: string) => void;
  undismissAll: () => void;
  setBeacons: (on: boolean) => void;
  markWelcomeSeen: () => void;
  startTour: (id: string) => void;
  setTourStep: (step: number) => void;
  endTour: (completed: boolean) => void;
  tickChecklist: (id: string, done?: boolean) => void;
  hideChecklist: (hidden: boolean) => void;
  showChecklist: () => void;
  resetAll: () => void;
}

function storageKey(userKey: string): string {
  return HELP_STORAGE_PREFIX + userKey;
}

export function readSnapshot(userKey: string): HelpSnapshot {
  try {
    const raw = localStorage.getItem(storageKey(userKey));
    if (!raw) return { ...EMPTY };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...EMPTY };
    const p = parsed as Partial<HelpSnapshot>;
    return {
      dismissed: isRecord(p.dismissed) ? (p.dismissed as Record<string, true>) : {},
      completedTours: isRecord(p.completedTours)
        ? (p.completedTours as Record<string, string>)
        : {},
      checklist: isRecord(p.checklist) ? (p.checklist as Record<string, true>) : {},
      checklistHidden: p.checklistHidden === true,
      checklistShown: p.checklistShown === true,
      welcomeSeen: p.welcomeSeen === true,
      beacons: p.beacons !== false,
      activeTour:
        p.activeTour && typeof p.activeTour === "object" && typeof p.activeTour.id === "string"
          ? { id: p.activeTour.id, step: Number(p.activeTour.step) || 0 }
          : null,
    };
  } catch {
    return { ...EMPTY };
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function writeSnapshot(userKey: string | null, snap: HelpSnapshot): void {
  if (!userKey) return;
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(snap));
  } catch {
    /* stockage indisponible : l'aide reste fonctionnelle, sans mémoire */
  }
}

function pick(state: HelpState): HelpSnapshot {
  return {
    dismissed: state.dismissed,
    completedTours: state.completedTours,
    checklist: state.checklist,
    checklistHidden: state.checklistHidden,
    checklistShown: state.checklistShown,
    welcomeSeen: state.welcomeSeen,
    beacons: state.beacons,
    activeTour: state.activeTour,
  };
}

export const useHelpStore = create<HelpState>((set, get) => {
  /** Applique une mutation puis persiste la photo courante. */
  const commit = (patch: Partial<HelpSnapshot>) => {
    set(patch);
    const s = get();
    writeSnapshot(s.userKey, pick(s));
  };
  return {
    ...EMPTY,
    userKey: null,
    hydrate: (userKey) => {
      if (get().userKey === userKey) return;
      set({ userKey, ...readSnapshot(userKey) });
    },
    dismiss: (topicId) => commit({ dismissed: { ...get().dismissed, [topicId]: true } }),
    undismissAll: () => commit({ dismissed: {}, beacons: true }),
    setBeacons: (on) => commit({ beacons: on }),
    markWelcomeSeen: () => commit({ welcomeSeen: true }),
    startTour: (id) => commit({ activeTour: { id, step: 0 }, welcomeSeen: true }),
    setTourStep: (step) => {
      const t = get().activeTour;
      if (t) commit({ activeTour: { ...t, step } });
    },
    endTour: (completed) => {
      const t = get().activeTour;
      const patch: Partial<HelpSnapshot> = { activeTour: null };
      if (completed && t) {
        patch.completedTours = { ...get().completedTours, [t.id]: new Date().toISOString() };
      }
      commit(patch);
    },
    tickChecklist: (id, done = true) => {
      const next = { ...get().checklist };
      if (done) next[id] = true;
      else delete next[id];
      commit({ checklist: next });
    },
    hideChecklist: (hidden) => commit({ checklistHidden: hidden }),
    showChecklist: () => commit({ checklistHidden: false, checklistShown: true }),
    resetAll: () => commit({ ...EMPTY }),
  };
});
